import React, { useEffect, useRef } from 'react';
import '../../../../css/common/chat/ChatLinkPreviewCard.css';

// LINE風リンクプレビュー（OGP / YouTube）カード。メッセージバブルの直下に出す。
// - OGP画像が無い場合は画像を出さず、タイトル/説明/ドメインでリンク先を示す（要件）。
// - タイトルは1行、はみ出しは … で省略（CSSで制御）。
// - YouTube はサムネイル＋赤い再生ボタン。タップで外部/新規タブへ遷移する。

const deriveDomain = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./i, '');
  } catch {
    return '';
  }
};

// 直近のスクロール可能な祖先を探す。
const findScrollableParent = (el) => {
  let node = el?.parentElement;
  while (node && node !== document.body) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight + 1) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
};

// 「新規位置（最下部）にいた場合だけ」、カードが後から増やした高さ分を追従スクロールする。
// column-reverse コンテナは CSS が自動で最下部を保つため触らない。
// メディアは aspect-ratio で高さ確保済みなので、マウント時にカード全高が確定している。
const followBottomIfNear = (el) => {
  const scroller = findScrollableParent(el);
  if (!scroller) return;
  if (window.getComputedStyle(scroller).flexDirection === 'column-reverse') return;
  const cardHeight = el.offsetHeight || 0;
  const distanceFromBottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
  // 追加された高さ（このカード分）＋わずかな余白以内なら「元々最下部にいた」とみなす。
  if (distanceFromBottom <= cardHeight + 80) {
    scroller.scrollTop = scroller.scrollHeight;
  }
};

export default function ChatLinkPreviewCard({ preview, autoFollow = true, displayFallback = false }) {
  const rootRef = useRef(null);
  const isDisplayable = Boolean(
    preview && preview.url && (displayFallback || preview.status === 'ok' || preview.status === 'empty')
  );

  // カード出現（マウント＝送信/受信の両方）で、最下部にいた場合のみ追従する。
  // モーダル等の一覧表示では autoFollow=false で無効化する。
  useEffect(() => {
    if (!autoFollow || !isDisplayable) return undefined;
    const raf = requestAnimationFrame(() => {
      if (rootRef.current) followBottomIfNear(rootRef.current);
    });
    return () => cancelAnimationFrame(raf);
  }, [autoFollow, isDisplayable]);

  if (!preview || !preview.url) return null;
  // 取得完了（ok / empty）のみ描画。pending / error は非表示。
  if (!displayFallback && preview.status !== 'ok' && preview.status !== 'empty') return null;

  const { url, kind, title, description, siteName, imageUrl } = preview;
  const domain = deriveDomain(url);
  const headline = title || domain || url;
  const metaLabel = siteName || domain;
  const isYoutube = kind === 'youtube';
  const hasImage = Boolean(imageUrl);

  const handleImageError = (event) => {
    // 外部OGP画像が死んでいる/ホットリンク不可のときは画像枠を畳んでテキストのみへ。
    const media = event.currentTarget.closest('.chat-link-preview__media');
    if (media) media.classList.add('chat-link-preview__media--broken');
  };

  return (
    <a
      ref={rootRef}
      className={`chat-link-preview${isYoutube ? ' chat-link-preview--youtube' : ''}`}
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={headline}
    >
      {hasImage ? (
        <span className="chat-link-preview__media">
          <img
            className="chat-link-preview__image"
            src={imageUrl}
            alt=""
            loading="lazy"
            onError={handleImageError}
            onLoad={() => { if (autoFollow) followBottomIfNear(rootRef.current); }}
          />
          {isYoutube ? (
            <span className="chat-link-preview__play" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22" focusable="false">
                <path d="M8 5v14l11-7z" fill="currentColor" />
              </svg>
            </span>
          ) : null}
        </span>
      ) : null}
      <span className="chat-link-preview__body">
        <span className="chat-link-preview__title">{headline}</span>
        {description ? <span className="chat-link-preview__desc">{description}</span> : null}
        {metaLabel ? (
          <span className="chat-link-preview__meta">
            {isYoutube ? (
              <svg className="chat-link-preview__glyph" viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
                <path d="M21.6 7.2a2.5 2.5 0 0 0-1.75-1.77C18.3 5 12 5 12 5s-6.3 0-7.85.43A2.5 2.5 0 0 0 2.4 7.2 26 26 0 0 0 2 12a26 26 0 0 0 .4 4.8 2.5 2.5 0 0 0 1.75 1.77C5.7 19 12 19 12 19s6.3 0 7.85-.43a2.5 2.5 0 0 0 1.75-1.77A26 26 0 0 0 22 12a26 26 0 0 0-.4-4.8ZM10 15V9l5 3Z" fill="currentColor" />
              </svg>
            ) : (
              <svg className="chat-link-preview__glyph" viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
                <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm6.9 6h-2.6a15.6 15.6 0 0 0-1.1-3.1A8 8 0 0 1 18.9 8ZM12 4c.8 0 1.7 1.4 2.3 4H9.7C10.3 5.4 11.2 4 12 4ZM4.3 14a8 8 0 0 1 0-4h3a20 20 0 0 0 0 4Zm.8 2h2.6c.3 1.1.7 2.1 1.1 3.1A8 8 0 0 1 5.1 16Zm2.6-8H5.1a8 8 0 0 1 3.7-3.1c-.4 1-.8 2-1.1 3.1ZM12 20c-.8 0-1.7-1.4-2.3-4h4.6C13.7 18.6 12.8 20 12 20Zm2.7-6H9.3a18 18 0 0 1 0-4h5.4a18 18 0 0 1 0 4Zm.4 5.1c.4-1 .8-2 1.1-3.1h2.6a8 8 0 0 1-3.7 3.1ZM16.7 14a20 20 0 0 0 0-4h3a8 8 0 0 1 0 4Z" fill="currentColor" />
              </svg>
            )}
            <span className="chat-link-preview__domain">{metaLabel}</span>
          </span>
        ) : null}
      </span>
    </a>
  );
}
